import { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';

export type GithubAuthMode = 'github_app' | 'pat';

export interface GithubConfig {
  authMode: GithubAuthMode;
  connected?: boolean;
  appId?: string;
  clientId?: string;
  webhookSecret?: string;
  patProfiles?: Array<{
    id: string;
    name: string;
    updatedAt?: unknown;
  }>;
  activePatProfileId?: string;
}

export interface GithubInstallation {
  id: number | string;
  accountLogin: string;
  accountType: string;
}

export interface GithubRepo {
  id: number;
  name: string;
  fullName: string;
  owner: string;
  private: boolean;
  defaultBranch: string;
  installationId?: number | string;
}

export interface GithubBranch {
  name: string;
  protected: boolean;
  sha: string;
  url: string;
  htmlUrl: string;
}

export interface GithubIssue {
  id: number;
  repoId?: number;
  number: number;
  title: string;
  state: string;
  url: string;
  htmlUrl: string;
  createdAt: string;
  updatedAt: string;
  assignees: string[];
  labels: string[];
}

export function useGithub() {
  const [config, setConfig] = useState<GithubConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const getGithubDataFn = useMemo(() => httpsCallable(functions, 'getGithubData'), []);
  const manageGithubConnectionFn = useMemo(() => httpsCallable(functions, 'manageGithubConnection'), []);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getGithubDataFn({ action: 'config' });
      setConfig((result.data || null) as GithubConfig | null);
      setError(null);
    } catch (err) {
      console.error('Error loading GitHub config:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [getGithubDataFn]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const saveConfig = useCallback(async (nextConfig: Partial<GithubConfig> & {
    authMode: GithubAuthMode;
    pat?: string;
    patName?: string;
    patProfileId?: string;
    privateKey?: string;
    clientSecret?: string;
  }) => {
    await manageGithubConnectionFn({
      action: 'saveConfig',
      config: nextConfig,
    });
    await loadConfig();
  }, [loadConfig, manageGithubConnectionFn]);

  const disconnect = useCallback(async () => {
    await manageGithubConnectionFn({ action: 'disconnect' });
    await loadConfig();
  }, [loadConfig, manageGithubConnectionFn]);

  const getInstallations = useCallback(async (): Promise<GithubInstallation[]> => {
    const result = await getGithubDataFn({ action: 'installations' });
    return result.data as GithubInstallation[];
  }, [getGithubDataFn]);

  const getRepos = useCallback(async (installationId?: string | number): Promise<GithubRepo[]> => {
    const result = await getGithubDataFn({
      action: 'repos',
      installationId,
    });
    return result.data as GithubRepo[];
  }, [getGithubDataFn]);

  const getClientRepo = useCallback(async (clientId: string): Promise<{
    githubRepoId: number;
    githubRepoOwner: string;
    githubRepoName: string;
    githubInstallationId?: number | string;
  }> => {
    const result = await getGithubDataFn({ action: 'clientRepo', clientId });
    return result.data as {
      githubRepoId: number;
      githubRepoOwner: string;
      githubRepoName: string;
      githubInstallationId?: number | string;
    };
  }, [getGithubDataFn]);

  const linkClientRepo = useCallback(async (clientId: string, repo: GithubRepo) => {
    await manageGithubConnectionFn({ action: 'linkClientRepo', clientId, repo });
  }, [manageGithubConnectionFn]);

  const unlinkClientRepo = useCallback(async (clientId: string) => {
    await manageGithubConnectionFn({ action: 'unlinkClientRepo', clientId });
  }, [manageGithubConnectionFn]);

  const setClientSyncOptions = useCallback(async (
    clientId: string,
    githubSync: { issuesIn?: boolean; branchesIn?: boolean; taskWriteBack?: boolean }
  ) => {
    await manageGithubConnectionFn({ action: 'setClientSyncOptions', clientId, githubSync });
  }, [manageGithubConnectionFn]);

  const upsertTaskIssue = useCallback(async (
    clientId: string,
    task: {
      taskId: string;
      title: string;
      description?: string;
      status?: string;
      priority?: string;
      dueDate?: string | Date | null;
      version?: string | null;
      issueNumber?: number;
    }
  ): Promise<{
    repoId: number;
    issueId: number;
    issueNumber: number;
    issueTitle: string;
    issueUrl: string;
    issueState: string;
  }> => {
    const result = await manageGithubConnectionFn({
      action: 'upsertTaskIssue',
      clientId,
      task,
    });
    return result.data as {
      repoId: number;
      issueId: number;
      issueNumber: number;
      issueTitle: string;
      issueUrl: string;
      issueState: string;
    };
  }, [manageGithubConnectionFn]);

  const syncClientRepo = useCallback(async (clientId: string) => {
    const result = await manageGithubConnectionFn({ action: 'syncRepo', clientId });
    return result.data as { repoId: number; branches: number; issues: number };
  }, [manageGithubConnectionFn]);

  const getBranches = useCallback(async (clientId: string): Promise<GithubBranch[]> => {
    const result = await getGithubDataFn({ action: 'branches', clientId });
    return result.data as GithubBranch[];
  }, [getGithubDataFn]);

  const getIssues = useCallback(async (clientId: string, state = 'open'): Promise<GithubIssue[]> => {
    const result = await getGithubDataFn({ action: 'issues', clientId, state });
    return result.data as GithubIssue[];
  }, [getGithubDataFn]);

  const getCachedBranches = useCallback(async (clientId: string): Promise<GithubBranch[]> => {
    const result = await getGithubDataFn({ action: 'cachedBranches', clientId });
    return result.data as GithubBranch[];
  }, [getGithubDataFn]);

  const getCachedIssues = useCallback(async (clientId: string): Promise<GithubIssue[]> => {
    const result = await getGithubDataFn({ action: 'cachedIssues', clientId });
    return result.data as GithubIssue[];
  }, [getGithubDataFn]);

  return {
    config,
    loading,
    error,
    loadConfig,
    saveConfig,
    disconnect,
    getInstallations,
    getRepos,
    getClientRepo,
    linkClientRepo,
    unlinkClientRepo,
    setClientSyncOptions,
    upsertTaskIssue,
    syncClientRepo,
    getBranches,
    getIssues,
    getCachedBranches,
    getCachedIssues,
  };
}
